<script lang="ts">
  /**
   * Ohjelma: pick the active plan (1400 / 1600), see its meals, the coach's
   * notes, and switch dark / light. Plan and theme both persist via settings.
   */
  import { errorMessage } from '../lib/errors';
  import { logout, getCurrentUser } from '../lib/auth';
  import { plans } from '../lib/plans.svelte';
  import { settings } from '../lib/settings.svelte';
  import { toast } from '../lib/toast.svelte';
  import type { Theme } from '../lib/types';
  import LoadState from '../components/LoadState.svelte';
  import Pill from '../components/Pill.svelte';
  import SegmentedControl from '../components/SegmentedControl.svelte';

  // Route params are accepted for the router's component type; this view has none.
  let _props: { params?: Record<string, string> } = $props();

  const COACH_NOTES = [
    'Lisää ruokaan suolaa (enintään 5 g päivässä). Muut mausteet ja yrtit vapaasti.',
    'Juo vähintään 2-3 litraa vettä päivässä, treenipäivinä enemmän.',
    'Viisi ateriaa joka päivä. Aterioiden paikkoja saa vaihtaa, mutta pidä välit tasaisina. Treenin jälkeen ateria tunnin sisällä.',
    'Valitse joka aterialle 1 hiilihydraatti, 1 proteiini ja 1 rasva sekä merkattu määrä vihanneksia. Jos hiilihydraattia ei ole merkattu, se tulee vihanneksista.',
    'Useampi saman kategorian raaka-aine: kertoimien summa on aina 1 (esim. 0,5 + 0,25 + 0,25).',
    'Kuivapaino on ilmoitettu ensin, kypsäpaino suluissa. Kumpaa tahansa saa käyttää.',
    'Suosi täysjyvää, vältä vaaleaa vehnää ja sokereita. Kahviin ja teehen maltillisesti rasvatonta maitoa tai kaurajuomaa.',
  ];

  const MODES: { id: Theme; label: string }[] = [
    { id: 'dark', label: 'Tumma' },
    { id: 'light', label: 'Vaalea' },
  ];

  let switching = $state(false);

  let ready = $derived(plans.loaded && settings.loaded);
  let loading = $derived(plans.loading || settings.loading);
  let error = $derived(plans.error ?? settings.error);
  let active = $derived(plans.byId(settings.planId));
  let planOptions = $derived(plans.list.map((p) => ({ id: p.id, label: p.title, sub: p.isDefault ? 'oletus' : 'vaihtoehto' })));

  function retry(): void {
    void plans.load(true);
    void settings.load(true);
  }

  async function choosePlan(id: string): Promise<void> {
    if (switching || id === settings.planId) return;
    switching = true;
    try {
      const s = await settings.setPlan(id);
      toast.show(`${plans.titleOf(s.planId)} käytössä`);
    } catch (err) {
      toast.show(errorMessage(err, 'Ohjelman vaihto epäonnistui'));
    } finally {
      switching = false;
    }
  }

  async function chooseMode(id: string): Promise<void> {
    try {
      await settings.setTheme(id as Theme);
    } catch (err) {
      toast.show(errorMessage(err, 'Ulkoasun tallennus epäonnistui'));
    }
  }

  let email = $derived(getCurrentUser()?.email ?? '');
</script>

<div class="topbar"><div class="grow"><div class="sub">Ravinto-ohjelmat</div><h1>Ohjelma</h1></div></div>

{#if !ready}
  <LoadState {loading} {error} onretry={retry} />
{:else}
  <div class="card plan-switch">
    <div class="eyebrow">Noudatan ohjelmaa</div>
    <SegmentedControl options={planOptions} value={settings.planId} disabled={switching} onchange={choosePlan} label="Noudatan ohjelmaa" />
    <div class="tip">Valinta pysyy päällä kunnes vaihdat sen. Vaihto koskee tätä päivää ja tulevia, menneet päivät säilyvät sellaisenaan.</div>
  </div>

  {#if active}
    <div class="card">
      <div class="row plan-head">
        <div class="grow">
          <div class="eyebrow">JRC Coaching</div>
          <div class="plan-name">{active.title}</div>
          <div class="plan-desc">{active.subtitle}</div>
        </div>
        <Pill tone="accent">Käytössä</Pill>
      </div>
      <div>
        {#each active.meals as m (m.id)}
          <div class="plan-meal">
            <div class="n">{m.mealNo}</div>
            <div>
              <div class="t">{m.name} <span class="hint">{m.hint}</span></div>
              <div class="tags">
                {#each m.sections as s (s.id)}
                  <span class="tag {s.kind}">{s.label} · {s.items.length} vaihtoehtoa</span>
                {/each}
                {#if m.vegTargetG !== null}
                  <span class="tag v">Vihannekset {m.vegTargetG} g</span>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <div class="note">Appi ei laske kaloreita, se seuraa vain että joka ateriasta on valittu ohjelman mukaiset osiot. Kun valmentaja lähettää uuden PDF:n, Tommi päivittää sen tänne.</div>

  <h2 class="section-title">Ulkoasu</h2>
  <div class="card">
    <SegmentedControl options={MODES} value={settings.theme} onchange={chooseMode} label="Ulkoasu" />
  </div>

  <h2 class="section-title">Valmentajan ohjeet</h2>
  <div class="card">
    <ul class="notes">
      {#each COACH_NOTES as n (n)}
        <li>{n}</li>
      {/each}
    </ul>
  </div>

  <div class="account">
    {#if email}<span class="email">{email}</span>{/if}
    <button type="button" class="link" onclick={() => logout()}>Kirjaudu ulos</button>
  </div>
{/if}

<style>
  .topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(var(--viisi-ground) 80%, transparent);
    padding: 18px 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.01em; flex: 1; }
  .topbar .sub { color: var(--viisi-ink-2); font-size: 13px; }
  .grow { flex: 1; min-width: 0; }
  .row { display: flex; align-items: center; gap: 10px; }
  .card {
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    padding: 14px;
    box-shadow: var(--viisi-shadow);
  }
  .section-title { margin: 22px 0 10px; font-size: 17px; font-weight: 700; }
  .eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--viisi-ink-3);
  }
  .tip { font-size: 12.5px; color: var(--viisi-ink-3); margin-top: 8px; }

  .plan-switch { margin-bottom: 12px; }
  .plan-switch .eyebrow { margin-bottom: 8px; }
  .plan-head { justify-content: space-between; margin-bottom: 4px; align-items: flex-start; }
  .plan-name { font-weight: 700; font-size: 17px; }
  .plan-desc { font-size: 12.5px; color: var(--viisi-ink-2); }

  .plan-meal {
    display: grid;
    grid-template-columns: 34px 1fr;
    gap: 10px;
    align-items: start;
    padding: 10px 0;
    border-bottom: 1px solid var(--viisi-line);
  }
  .plan-meal:last-child { border-bottom: 0; }
  .plan-meal .n {
    width: 34px;
    height: 34px;
    border-radius: var(--viisi-radius-sm);
    background: var(--viisi-surface-2);
    display: grid;
    place-items: center;
    font-family: var(--viisi-font-display);
    font-weight: 800;
  }
  .plan-meal .t { font-weight: 700; }
  .hint { font-weight: 500; color: var(--viisi-ink-3); font-size: 12px; }
  .tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 4px; }
  .tag {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 6px;
    background: var(--viisi-surface-2);
    color: var(--viisi-ink-2);
  }
  .tag.h { background: color-mix(in srgb, var(--viisi-c-carb) 16%, transparent); color: var(--viisi-c-carb); }
  .tag.p { background: color-mix(in srgb, var(--viisi-c-prot) 16%, transparent); color: var(--viisi-c-prot); }
  .tag.r { background: color-mix(in srgb, var(--viisi-c-fat) 16%, transparent); color: var(--viisi-c-fat); }
  .tag.v { background: color-mix(in srgb, var(--viisi-c-veg) 16%, transparent); color: var(--viisi-c-veg); }

  .note {
    font-size: 13px;
    color: var(--viisi-ink-2);
    padding: 12px 14px;
    background: var(--viisi-surface-2);
    border-radius: 12px;
    margin-top: 12px;
  }
  .notes {
    margin: 0;
    padding-left: 18px;
    font-size: 13.5px;
    color: var(--viisi-ink-2);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .account {
    margin-top: 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 12.5px;
    color: var(--viisi-ink-3);
  }
  .email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
  .link { color: var(--viisi-ink-2); font-weight: 600; font-size: 12.5px; text-decoration: underline; flex: none; }
</style>
